import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import Dialog from '~components/ui/dialog'
import ProgressBar from '~components/ui/progress-bar'
import TextField from '~components/ui/text-input/text-field'
import useOwnerName from '~hooks/use-owner-name'
import Pet from '~models/pet'
import useCreatePet from '~repositories/hooks/use-create-pet'
import useMyProfile from '~repositories/hooks/use-my-profile'
import useUpdateMyProfile from '~repositories/hooks/use-update-my-profile'
import Mixpanel from '~utils/mixpanel'

import PetAge from './components/pet-input/pet-age'
import PetGender from './components/pet-input/pet-gender'
import PetName from './components/pet-input/pet-name'
import PetType from './components/pet-input/pet-type'
import usePetInfo from './hooks/use-pet-info'
import { PetCreateStep } from './types'

type PetCreateDialogProps = Readonly<{
  open: boolean
  location: 'askavet' | 'profile' | 'medical-records'
  onRequestClose: () => void
}>

const TOTAL_STEP_OF_ADD_PET = 4

const CreatePetDialog = ({ open, onRequestClose, location }: PetCreateDialogProps) => {
  const [step, setStep] = useState(PetCreateStep.PetName)
  const { data: myProfile } = useMyProfile()
  const [showOwnerNicknameInput, setShowOwnerNicknameInput] = useState<boolean>(false)

  const {
    ownerName,
    ownerNameHelperMessage,
    hasOwnerNameError,
    hasOwnerNameChanged,
    isLoading,
    handleOwnerName,
    setOwnerNameFocused,
  } = useOwnerName()

  const shouldChangeOwnerNickname = myProfile?.default_nickname === myProfile?.nickname

  const getDisplayedNickname = () => {
    if (shouldChangeOwnerNickname) {
      return hasOwnerNameChanged ? ownerName : ''
    }

    return myProfile?.default_nickname
  }

  const shouldUpdateOwnerNickname = myProfile?.nickname !== ownerName

  const { mutate: updateMyProfile } = useUpdateMyProfile()

  const { pet, nameErrorMessage, ageErrorMessage, isAgeValidating, formattedAgeDate, setPet } =
    usePetInfo()

  const { name, gender, species, breed, age, isNeutered } = pet

  const createPet = useCreatePet({
    onSuccess: onRequestClose,
  })

  const createNewPet = () => {
    createPet({
      ...new Pet.Level1(),
      name,
      gender,
      is_neutered: isNeutered,
      kind: { depth1: species, depth2: breed },
      born_at: new Date(formattedAgeDate).toISOString(),
      born_year: new Date(formattedAgeDate).getFullYear(),
    })
  }

  const onConfirm = () => {
    switch (step) {
      case PetCreateStep.PetName: {
        if (shouldChangeOwnerNickname && !showOwnerNicknameInput) {
          setShowOwnerNicknameInput(true)
          return
        }

        Mixpanel.logEvent('pet_name_info: submitted', {
          location,
        })
        return setStep(() => PetCreateStep.PetType)
      }
      case PetCreateStep.PetType: {
        Mixpanel.logEvent('pet_breed_info: submitted', {
          breed_type: species,
          breed_detail: breed,
          location,
        })
        return setStep(() => PetCreateStep.Gender)
      }
      case PetCreateStep.Gender: {
        Mixpanel.logEvent('pet_gender_info: submitted', {
          gender_type: gender,
          spayed: isNeutered,
          location,
        })
        return setStep(() => PetCreateStep.Age)
      }
      case PetCreateStep.Age: {
        Mixpanel.logEvent('pet_age_info: submitted', {
          age_detail: age,
          location,
        })

        if (shouldUpdateOwnerNickname) {
          updateMyProfile({
            nickname: ownerName,
          })
        }

        return createNewPet()
      }
      default:
    }
  }

  const isPetNameStepConfirmDisabled = useCallback(() => {
    if (shouldChangeOwnerNickname && showOwnerNicknameInput) {
      return !hasOwnerNameChanged || !ownerName?.length || hasOwnerNameError || isLoading
    }

    return !name?.length || !!nameErrorMessage?.length
  }, [
    hasOwnerNameChanged,
    hasOwnerNameError,
    isLoading,
    name?.length,
    nameErrorMessage?.length,
    shouldChangeOwnerNickname,
    showOwnerNicknameInput,
    ownerName?.length,
  ])

  const isConfirmDisabled = useMemo(
    () => ({
      [PetCreateStep.PetName]: isPetNameStepConfirmDisabled(),
      [PetCreateStep.PetType]: !species || !breed,
      [PetCreateStep.Gender]: gender === null || isNeutered === null,
      [PetCreateStep.Age]: !formattedAgeDate || !!ageErrorMessage?.length || isAgeValidating,
    }),
    [
      formattedAgeDate,
      ageErrorMessage?.length,
      breed,
      gender,
      isAgeValidating,
      isNeutered,
      isPetNameStepConfirmDisabled,
      species,
    ]
  )

  useEffect(() => {
    if (!open) {
      setStep(() => PetCreateStep.PetName)
      setPet({ type: 'RESET' })
      handleOwnerName('')
      setShowOwnerNicknameInput(false)
    } else {
      handleOwnerName(myProfile?.nickname || '')
    }
  }, [handleOwnerName, myProfile?.nickname, open, setPet])

  return (
    <Dialog open={open} className="w-full dark:bg-gray-900">
      <Dialog.CloseIcon onClose={onRequestClose} />
      <Dialog.Content className="lg:min-h-[348px]">
        <p className="pb-4 text-center text-gray-400 lg:text-gray-300 text-body-2">
          Step {step} of {TOTAL_STEP_OF_ADD_PET}
        </p>
        <ProgressBar total={TOTAL_STEP_OF_ADD_PET} currentStep={step} className="mb-5" />
        {step === PetCreateStep.PetName && (
          <div className="flex flex-col items-center">
            <h3 className="py-4 text-center text-headline-3 dark:text-white text-gray-900">
              {showOwnerNicknameInput ? 'What is your name?' : 'What is your pet’s name?'}
            </h3>
            <div className="flex w-full flex-col items-center py-6">
              {showOwnerNicknameInput && (
                <TextField
                  value={getDisplayedNickname()}
                  showHelper
                  helperMessage={ownerNameHelperMessage}
                  error={hasOwnerNameError}
                  onChange={e => {
                    handleOwnerName(e.target.value)
                  }}
                  label="Owner name"
                  className="mb-5 w-full lg:w-[272px]"
                  autoFocus={showOwnerNicknameInput}
                  onFocus={() => {
                    setOwnerNameFocused(true)
                  }}
                  onBlur={() => {
                    setOwnerNameFocused(false)
                  }}
                />
              )}
              <div className="w-full lg:w-[272px]">
                <PetName
                  value={name || ''}
                  errorMessage={nameErrorMessage}
                  onNameChange={name => {
                    setPet({ type: 'SET_NAME', payload: name })
                  }}
                  label="Pet name"
                />
              </div>
            </div>
          </div>
        )}

        {step === PetCreateStep.PetType && (
          <div>
            <h3 className="py-4 text-center text-headline-3 dark:text-white text-gray-900">{`Who is ${name}?`}</h3>
            <PetType
              species={species}
              breed={breed}
              onBreedSelect={breed => {
                setPet({ type: 'SET_BREED', payload: breed })
              }}
              onSpeciesSelect={species => {
                setPet({ type: 'SET_SPECIES', payload: species })
              }}
              className="py-6"
            />
          </div>
        )}
        {step === PetCreateStep.Gender && (
          <div>
            <h3 className="py-4 text-center text-headline-3 dark:text-white text-gray-900">{`Is ${name} she or he?`}</h3>
            <PetGender
              gender={gender}
              isNeutered={isNeutered}
              onGenderSelect={gender => {
                setPet({ type: 'SET_GENDER', payload: gender })
              }}
              onNeuteredStatusSelect={status => {
                setPet({ type: 'SET_IS_NEUTERED', payload: status })
              }}
              className="py-6"
            />
          </div>
        )}
        {step === PetCreateStep.Age && (
          <div className="w-full lg:w-full">
            <h3 className="py-4 text-center text-headline-3 dark:text-white text-gray-900">
              When is {name}'s birthday?
            </h3>
            <p className="whitespace-pre-line text-center text-gray-500 text-body-2 lg:whitespace-normal">{`Not sure? Make a best guess.\nDon’t have one? Make one today!`}</p>
            <div className="py-6">
              <PetAge
                label="Birthday"
                age={formattedAgeDate}
                onAgeChange={age => {
                  setPet({ type: 'SET_AGE', payload: age })
                }}
                ageErrorMessage={ageErrorMessage}
              />
            </div>
          </div>
        )}
      </Dialog.Content>
      <Dialog.Action>
        <Dialog.Button
          onClick={onConfirm}
          disabled={isConfirmDisabled[step]}
          className="lg:w-full dark:disabled:bg-gray-800"
        >
          {step === PetCreateStep.Age ? 'Done' : 'Continue'}
        </Dialog.Button>
      </Dialog.Action>
    </Dialog>
  )
}

export default memo(CreatePetDialog)
